import { digger } from '@cbre/digger';
import { addition, reduceAmountWith, ZERO_SF } from '@shared/utils/helpers/uom-calculator';
import { Amount } from 'uom';
import { Quantity } from 'uom-units';
import { Usage, UsageDef } from './usage';

const isPrimaryUsage = ({ primary_usage_f }) => primary_usage_f;

/**
 * Returns usage with primary usage
 * @param usages: `object[]`
 * @returns `object`
 */
export const byPrimaryUsage = usages => (usages || []).find(isPrimaryUsage);

/**
 * Helper fn to returns first available primary usage, or available or empty object
 * @param { any[] } usages list of usages
 * @returns {any} usage
 *
 * NOTE: ternary operator must be wrapped on parenthesis, otherwise logic will
 * be corrupted due to OR statement being considered as part of ternary
 * operator.
 * i.e. byPrimaryUsage(usages) || usages.length as one expression vs
 * byPrimaryUsage(usages) || (usages.length ? usages[0] : Usage({})) as two
 * different expressions
 *
 */
export const byPrimaryUsageOrFirstOrEmpty = (usages): UsageDef =>
  byPrimaryUsage(usages) || (usages != null && usages.length ? usages[0] : Usage({}));
/**
 * Helper function to returns first usage that matches a value for an provided path
 *
 * @param usages List of usages
 * @param path Path to get data from
 * @param valueToMatch Value to compare
 */
export const byCustomField = (usages, path: string, valueToMatch: string | number) =>
  usages.find(usage => digger(usage, path) === valueToMatch);

export interface WithAlternateUsageDef {
  /**
   * Finds first non primary usage (if any)
   * @returns {UsageDef} UsageDef
   *
   * @see withAlternateUsage
   */
  findOneNonPrimaryUsage: () => UsageDef;
  /**
   * Finds first non primary usage or empty in case not found
   * @returns {UsageDef} UsageDef
   * @see withAlternateUsage
   */
  findOneNonPrimaryUsageOrEmpty: () => UsageDef | {};
}
/**
 * Composer to add behavioral aggregations to find one NON-PRIMARY usage
 * @returns WithAlternateUsageDef
 */
const withAlternateUsage = (): WithAlternateUsageDef => {
  function findOneNonPrimaryUsage() {
    return (this.usages || []).find(usage => !isPrimaryUsage(usage));
  }
  function findOneNonPrimaryUsageOrEmpty() {
    return (this.usages || []).find(usage => !isPrimaryUsage(usage)) || {};
  }
  return {
    findOneNonPrimaryUsage,
    findOneNonPrimaryUsageOrEmpty,
  };
};

/**
 * composer interface for `withUsagesHelpers`
 */
export interface WithUsagesHelpersDef {
  primaryUsageGeoMarkets: () => { [key: string]: any };
  primaryUsage: () => UsageDef;
  primaryUsageOrFirstOrEmpty: () => UsageDef;
  findUsageByName: (usageType: string) => UsageDef;
  getUsageContacts: () => Array<{ [key: string]: any }>;
  getUsageBy: (path: string, valueToMatch: string | number) => UsageDef;
  getUsageById: (usageId: number) => UsageDef;
}
/**
 * Usages object composer aggregates usage helpers to retrieve
 *  - primary usage
 *  - primary usage or first or empty or first or empty
 *  - primary usage geo market information
 *  - all contacts from all users
 */
export const withUsagesHelpers = (): WithUsagesHelpersDef => {
  function primaryUsageGeoMarkets() {
    return byPrimaryUsageOrFirstOrEmpty(this.usages).getGeography();
  }

  function primaryUsage(): UsageDef {
    return byPrimaryUsage(this.usages);
  }

  function primaryUsageOrFirstOrEmpty(): UsageDef {
    return byPrimaryUsageOrFirstOrEmpty(this.usages);
  }

  function findUsageByName(usageType: string): UsageDef {
    return (this.usages || []).find(usage => usageType === usage.ref_property_usage_type_desc);
  }

  function getUsageContacts(): Array<{ [key: string]: any }> {
    return (this.usages || []).reduce((acc, usage) => [...acc, ...usage.companyContacts()], []);
  }

  function getUsageBy(path: string, valueToMatch: string | number): UsageDef {
    return byCustomField(this.usages || [], path, valueToMatch);
  }

  function getUsageById(usageId: number): UsageDef {
    return this.getUsageBy('entity.id', usageId);
  }

  return {
    getUsageContacts,
    primaryUsageOrFirstOrEmpty,
    primaryUsage,
    primaryUsageGeoMarkets,
    getUsageBy,
    getUsageById,
    findUsageByName,
  };
};

/**
 * composer interface for `withVacantArea`
 */
export interface WithVacantAreaDef {
  /**
   * Returns the summary of all usages.total_vacant_area
   */
  getVacantArea: () => Amount.Amount<Quantity.Area | Quantity.Length>;
}

/**
 * Behavioral aggregation to sum `total_vacant_area` with it's respective `uom`
 * from all available usages
 */
export const withVacantArea = (): WithVacantAreaDef => {
  function getVacantArea(): Amount.Amount<Quantity.Area | Quantity.Length> {
    return reduceAmountWith(this.usages || [], {
      dataset: { value: 'total_vacant_area', uom: 'total_vacant_area_uom_desc' },
      operation: addition,
      initialValue: ZERO_SF,
    });
  }
  return { getVacantArea };
};

/**
 * composer interface for `withAvailableArea`
 */
export interface WithAvailableAreaDef {
  /**
   * Returns the summary of all usages.total_available_surface_area
   */
  getAvailableArea: () => Amount.Amount<Quantity.Area | Quantity.Length>;
}
/**
 * Behavioral aggregation to sum `total_available_surface_area` with it's respective `uom`
 * from all available usages
 */
export const withAvailableArea = (): WithAvailableAreaDef => {
  function getAvailableArea(): Amount.Amount<Quantity.Area | Quantity.Length> {
    return reduceAmountWith(this.usages || [], {
      dataset: { value: 'total_available_surface_area', uom: 'total_available_surface_area_uom_desc' },
      operation: addition,
      initialValue: ZERO_SF,
    });
  }
  return { getAvailableArea };
};

/**
 * composer interface for `withUsages`
 * @see withUsages
 */
export interface WithUsagesDef
  extends WithUsagesHelpersDef, WithAlternateUsageDef, WithAvailableAreaDef, WithVacantAreaDef {
  usages: UsageDef[];
}

/**
 * Returns composer object with usages helpers
 * @param state
 */
export const withUsages = (state): WithUsagesDef => {
  return Object.assign(
    {},
    state,
    { usages: (state.usages || []).map(Usage) },
    // composers
    withUsagesHelpers(),
    withAlternateUsage(),
    withVacantArea(),
    withAvailableArea()
  );
};
